/**
 * @file App.tsx
 * @description Root application component.
 *
 * Responsibilities:
 *  - Auth gate: shows LoginPage until user is authenticated
 *  - Layout: Sidebar (fixed left) + Header (fixed top) + scrollable main content
 *  - View routing: switches between the 3 views based on sidebar navigation
 *  - Data orchestration: fetches from all 3 Firestore hooks and passes down
 */

import { useEffect, useState } from "react";
import { LoginPage } from "./components/Auth/LoginPage";
import { Sidebar } from "./components/Layout/Sidebar";
import { Header } from "./components/Layout/Header";
import { OpsOverview } from "./components/Views/OpsOverview";
import { DataHealth } from "./components/Views/DataHealth";
import { DataIngest } from "./components/Views/DataIngest";
import { UserManagement } from "./components/Views/UserManagement";
import { ClinicalReference } from "./components/Views/ClinicalReference";
import { PatientIntelligence } from "./components/Views/PatientIntelligence";

import { DoctorDashboard } from "./components/Views/Dashboards/DoctorDashboard";
import { NurseDashboard } from "./components/Views/Dashboards/NurseDashboard";
import { LabDashboard } from "./components/Views/Dashboards/LabDashboard";
import { DeskDashboard } from "./components/Views/Dashboards/DeskDashboard";
import { DeptDashboard } from "./components/Views/Dashboards/DeptDashboard";
import { ManagementDashboard } from "./components/Views/Dashboards/ManagementDashboard";
import { BillingDashboard } from "./components/Views/Dashboards/BillingDashboard";
import { PaymentDashboard } from "./components/Views/Dashboards/PaymentDashboard";
import { AlertsDashboard } from "./components/Views/Dashboards/AlertsDashboard";

import { useAuth } from "./hooks/useAuth";
import { useUnifiedView } from "./hooks/useUnifiedView";
import { useAuditLog } from "./hooks/useAuditLog";
import { useSystemMetrics } from "./hooks/useSystemMetrics";
import { AppView } from "./types";
import { getRoleForEmail, ROLE_CONFIGS } from "./config/roles";
import { AlertProvider } from "./contexts/AlertContext";
import { AlertOverlay } from "./components/Layout/AlertOverlay";

export default function App() {
  // ─── Auth ──────────────────────────────────────────────────────────────────
  const { user, loading: authLoading, signIn, signOut, authError } = useAuth();

  const userRole = getRoleForEmail(user?.email);
  const roleConfig = ROLE_CONFIGS[userRole];

  // ─── Navigation ────────────────────────────────────────────────────────────
  const [activeView, setActiveView] = useState<AppView>(roleConfig.defaultView);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);

  const handleNavigate = (view: AppView, params?: any) => {
    setActiveView(view);
    if (params?.patientId) {
      setSelectedPatientId(params.patientId);
    }
  };

  // Set default view on user change
  useEffect(() => {
    if (user) {
      setActiveView(ROLE_CONFIGS[getRoleForEmail(user.email)].defaultView);
    }
  }, [user]);

  // ─── Data ─────────────────────────────────────────────────────────────────
  const { records, loading: recordsLoading, isMock } = useUnifiedView();
  const { entries: auditEntries, loading: auditLoading } = useAuditLog(200);
  const { metrics, loading: metricsLoading } = useSystemMetrics();

  const conflictCount = auditEntries.length;
  const lastReconciled = metrics?.computed_at ?? null;

  // ─── Auth Loading ──────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-navy flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
          <p className="text-gray-500 text-sm">Loading HospOps Central...</p>
        </div>
      </div>
    );
  }

  // ─── Login Gate ────────────────────────────────────────────────────────────
  // In mock mode (no real Firebase project configured), bypass auth
  const MOCK_MODE =
    !import.meta.env.VITE_FIREBASE_PROJECT_ID ||
    import.meta.env.VITE_FIREBASE_PROJECT_ID === "your-project-id";

  if (!user && !MOCK_MODE) {
    return (
      <LoginPage
        onSignIn={signIn}
        error={authError}
        loading={authLoading}
      />
    );
  }

  // ─── Render active view ────────────────────────────────────────────────────
  const renderView = () => {
    switch (activeView) {
      case "overview":
        return (
          <OpsOverview
            records={records}
            metrics={metrics}
            loading={recordsLoading || metricsLoading}
          />
        );
      case "data-health":
        return (
          <DataHealth
            entries={auditEntries}
            metrics={metrics}
            loading={auditLoading || metricsLoading}
          />
        );
      case "ingest":
        return <DataIngest />;
      case "users":
        return <UserManagement />;
      case "clinical":
        return <ClinicalReference />;
      case "doctor-dash":
        return <DoctorDashboard records={records} metrics={metrics} loading={recordsLoading} onNavigate={handleNavigate} />;
      case "patient-intel":
        return <PatientIntelligence records={records} selectedPatientId={selectedPatientId} onNavigate={handleNavigate} />;
      case "nurse-dash":
        return <NurseDashboard records={records} metrics={metrics} loading={recordsLoading} />;
      case "lab-dash":
        return <LabDashboard records={records} metrics={metrics} loading={recordsLoading} />;
      case "desk-dash":
        return <DeskDashboard records={records} metrics={metrics} loading={recordsLoading} />;
      case "dept-dash":
        return <DeptDashboard records={records} metrics={metrics} loading={recordsLoading} />;
      case "mgmt-dash":
        return <ManagementDashboard records={records} metrics={metrics} loading={metricsLoading} />;
      case "billing":
        return <BillingDashboard records={records} metrics={metrics} loading={recordsLoading} />;
      case "payment-dash":
        return <PaymentDashboard records={records} />;
      case "alerts-dash":
        return <AlertsDashboard records={records} />;
      default:
        return null;
    }
  };

  return (
    <AlertProvider>
      <div className="min-h-screen bg-navy-950">
        <AlertOverlay />
        
        {/* Fixed sidebar */}
      <Sidebar
        activeView={activeView}
        onNavigate={handleNavigate}
        userEmail={user?.email ?? null}
        onSignOut={signOut}
        conflictCount={conflictCount}
        lastReconciled={lastReconciled}
        isMock={isMock || MOCK_MODE}
      />

      {/* Fixed header — offset by sidebar width */}
      <Header activeView={activeView} />

      {/* Scrollable main content area */}
      <main
        className="min-h-screen pt-16 p-4 md:p-6 ml-0 md:ml-64 pb-24 md:pb-6 transition-all duration-300"
      >
        <div className="max-w-7xl mx-auto">
          {renderView()}
        </div>
      </main>
    </div>
    </AlertProvider>
  );
}
